import { useEffect, useRef, useState } from 'react';
import { Avatar } from '@/components/Avatar';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Skeleton } from '@/ui/Skeleton';
import { FalloDePanel } from '@/components/Panel';
import { ContenidoRico } from './piezas';
import { DialogoDenunciar } from './Publicacion';
import { useBorrarComentario, useComentar, useComentarios } from './api';

/**
 * Comentarios — el hilo de una publicación.
 *
 * Se carga **al abrirlo**, no al pintar la tarjeta: veinte publicaciones en
 * pantalla son veinte hilos que casi nadie va a desplegar, y pedirlos todos al
 * entrar multiplicaba por veinte las peticiones del muro.
 *
 * En una publicación retirada el hilo nace abierto y **sin caja para escribir**:
 * los comentarios que ya están se siguen viendo —esa es la razón de ser de la
 * lápida— pero la conversación queda cerrada.
 */
export function Comentarios({ postId, cerrada }: { postId: number; cerrada: boolean }) {
  const consulta = useComentarios(postId, true);
  const yo = Auth.getUser();
  const [aBorrar, setABorrar] = useState<number | null>(null);
  const [aDenunciar, setADenunciar] = useState<number | null>(null);
  const borrar = useBorrarComentario(postId);

  return (
    <div className="border-t border-line bg-sunken/60 px-4 py-3">
      {consulta.isPending ? (
        <Skeleton className="h-10" />
      ) : consulta.isError ? (
        <FalloDePanel
          que="los comentarios"
          err={consulta.error}
          onReintentar={() => void consulta.refetch()}
        />
      ) : !consulta.data?.length ? (
        <p className="py-1 text-center text-2 text-muted">Sin comentarios aún</p>
      ) : (
        <ul className="mb-3 flex flex-col gap-2.5">
          {consulta.data.map((c) => {
            const mio = yo?.id != null && yo.id === c.author_id;
            return (
              <li key={c.id} className="flex gap-2">
                <Avatar nombre={c.author_name ?? 'Usuario'} tamano={32} />
                <div className="min-w-0 flex-1 rounded-md border border-line bg-surface px-3 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="min-w-0 truncate text-2 font-bold text-ink">
                      {c.author_name || 'Usuario'}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      className="-my-1 size-7"
                      aria-label={mio ? 'Eliminar mi comentario' : 'Reportar comentario'}
                      title={mio ? 'Eliminar mi comentario' : 'Reportar comentario'}
                      onClick={() => (mio ? setABorrar(c.id) : setADenunciar(c.id))}
                    >
                      <i className={`bi ${mio ? 'bi-trash3' : 'bi-flag'} text-1`} aria-hidden="true" />
                    </Button>
                  </div>
                  <ContenidoRico html={c.content} className="text-ink-2" />
                  <p className="mt-1 text-1 text-muted">{Utils.relativeTime(c.created_at)}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!cerrada && <CajaDeComentario postId={postId} />}

      {aBorrar != null && (
        <Dialog
          abierto
          onCambio={() => setABorrar(null)}
          titulo="Eliminar comentario"
          descripcion="Se quitará del hilo. Queda registrado en la auditoría con su texto original. No se puede deshacer."
          ancho="max-w-md"
          pie={
            <>
              <Button variant="ghost" onClick={() => setABorrar(null)} autoFocus>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                loading={borrar.isPending}
                onClick={() =>
                  borrar.mutate(aBorrar, {
                    onSuccess: () => {
                      Utils.toast('Comentario eliminado', 'success');
                      setABorrar(null);
                    },
                    onError: (e: unknown) =>
                      Utils.toast((e as Error).message || 'No se pudo eliminar', 'error'),
                  })
                }
              >
                Eliminar
              </Button>
            </>
          }
        />
      )}

      {aDenunciar != null && (
        <DialogoDenunciar comentarioId={aDenunciar} onCerrar={() => setADenunciar(null)} />
      )}
    </div>
  );
}

/* ── Escribir un comentario ────────────────────────────────────────────── */

/**
 * La caja de escribir, con menciones y Enter para enviar.
 *
 * Es un `contenteditable` porque tiene que aceptar el chip de mención que
 * inserta `mentions.js`. React no gobierna su contenido —ver `EditorRico`— y se
 * vacía subiendo la `versión` después de enviar.
 *
 * Enter envía y Shift+Enter hace salto de línea, salvo si el desplegable de
 * menciones está abierto: ahí Enter es «elegir a esta persona», y `mentions.js`
 * lo intercepta en fase de captura para llegar antes que esto.
 */
function CajaDeComentario({ postId }: { postId: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [version, setVersion] = useState(0);
  const [vacio, setVacio] = useState(true);
  const comentar = useComentar(postId);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = '';
    Mentions.enable(el);
  }, [version]);

  const enviar = () => {
    const el = ref.current;
    if (!el) return;
    const texto = (el.innerText || '').trim();
    if (!texto) return;
    // El mismo aviso que ya da el compositor, y contra la misma lista viva del
    // servidor: sin él, el usuario descubre el veto con un 400 seco.
    if (Utils.findProfanity(texto)) {
      Utils.toast('Tu comentario contiene lenguaje no permitido.', 'error', 4000);
      return;
    }
    comentar.mutate(Utils.sanitizeRich(el.innerHTML).trim(), {
      onSuccess: () => {
        setVersion((v) => v + 1);
        setVacio(true);
      },
      onError: (e: unknown) =>
        Utils.toast((e as Error).message || 'No se pudo comentar', 'error'),
    });
  };

  return (
    <div className="flex items-end gap-2">
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Escribe un comentario"
        data-marcador="Escribe un comentario… (usa @ para etiquetar)"
        onInput={(e) => setVacio(!(e.currentTarget.innerText || '').trim())}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || e.shiftKey) return;
          // Si el desplegable de menciones está abierto, Enter es suyo.
          const menciones = document.querySelector<HTMLElement>('.mention-dropdown');
          if (menciones && menciones.style.display !== 'none') return;
          e.preventDefault();
          enviar();
        }}
        className="editor-rico min-h-10 flex-1 rounded-full border border-field-border bg-surface
                   px-4 py-2 text-3 text-ink focus-visible:focus-anillo"
      />
      <Button
        size="sm"
        iconOnly
        className="size-10 shrink-0 rounded-full"
        aria-label="Enviar comentario"
        loading={comentar.isPending}
        disabled={vacio}
        razonDeshabilitado="Escribe algo para poder enviarlo"
        onClick={enviar}
      >
        <i className="bi bi-send-fill" aria-hidden="true" />
      </Button>
    </div>
  );
}
